import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, ArrowRight, CheckCircle2, AlertTriangle, Loader2, Lock, Save } from 'lucide-react';
import { toast } from 'sonner';
import { historicalConversionService } from '../services';
import { SnapshotSourceStep, SnapshotClosingStep, SnapshotYtdStep, SnapshotSiteStep, GapReview } from '../components/legacy-snapshot/SnapshotSteps';
import { SourceUnavailablePanel, SourceBindingTable } from '../components/legacy-snapshot/SourceUnavailablePanel';
import { SourceSummary } from '../components/legacy-snapshot/SourceSummary';
import { matchingPath } from '../lib/importResume';

const STEPS = ['Source & Audit', 'Closing Stock', 'Previous YTD', 'Site Inventory & Bar Prices'];

const CHECK_LABELS = {
  audit_dates_present: 'The audit dates are in the source',
  close_date_matches_valuation_date: 'The closing date matches the valuation date',
  closing_fields_present: 'Every product has a closing quantity and value',
  movement_identity_holds: 'Opening + deliveries − credits − closing = sales for every product',
  closing_total_matches_bar_stock_total: 'Closing value matches the bar stock total',
  previous_audit_identified: 'The previous audit is identified',
  opening_equals_previous_audit_closing: "Opening value equals the previous audit's closing value",
  structure_supported: 'This stock structure is recognised but not yet supported for publishing',
  all_ledgers_in_bar_file: 'Every stock ledger in the transactions is listed in the bar file',
  buckets_mapped_to_site_locations: "Each stock ledger matches one of the site's audit locations (cellar / bar)",
};

const Stepper = ({ step, setStep }) => (
  <div className="flex flex-wrap gap-2" data-testid="snapshot-stepper">
    {STEPS.map((label, i) => (
      <button
        key={label}
        type="button"
        onClick={() => setStep(i + 1)}
        data-testid={`snapshot-step-${i + 1}`}
        className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${step === i + 1 ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
      >
        {i + 1}. {label}
      </button>
    ))}
  </div>
);

const ProofPanel = ({ evidence }) => {
  if (evidence.verified) return null;
  const failed = Object.entries(evidence.checks).filter(([, ok]) => !ok);
  return (
    <Alert variant="destructive" data-testid="snapshot-not-proven">
      <AlertTriangle className="h-4 w-4" />
      <AlertDescription>
        <strong>The closing stock can't be confirmed from this source, so it can't be published.</strong>
        <ul className="mt-1 list-disc pl-5 text-sm">
          {failed.map(([k]) => <li key={k}>{CHECK_LABELS[k] || k}</li>)}
        </ul>
      </AlertDescription>
    </Alert>
  );
};

const StatusBanner = ({ saved }) => {
  const pub = saved.find((s) => s.status === 'published');
  if (!pub) return null;
  return (
    <Alert className="border-emerald-300 bg-emerald-50" data-testid="snapshot-published-banner">
      <Lock className="h-4 w-4 text-emerald-700" />
      <AlertDescription>
        <strong>Snapshot published and frozen.</strong>{' '}
        {pub.target_session_id
          ? <>Used as opening stock for <Link className="underline" to={`/stocktaking/sessions/${pub.target_session_id}`} data-testid="snapshot-carried-session-link">session {pub.target_session_id}</Link>.</>
          : 'It will be used once, as the opening stock and site setup for the next new stocktake at this site.'}
      </AlertDescription>
    </Alert>
  );
};

export default function LegacyClosingSnapshotPage() {
  const [params] = useSearchParams();
  const conversionId = params.get('conversion_id');
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(null);
  const [ack, setAck] = useState(false);

  const load = () => historicalConversionService.getClosingSnapshot(conversionId)
    .then((d) => {
      setData(d);
      const draft = d.saved.find((s) => s.status === 'draft');
      const current = d.saved.find((s) => s.status === 'published') || draft;
      if (draft?.review_step) setStep(draft.review_step);
      setAck(!!current?.review_decision?.gaps_acknowledged);
    })
    .catch((e) => setError(e?.response?.data?.detail || 'Could not load the snapshot'));
  const [binding, setBinding] = useState(null);

  useEffect(() => {
    if (!conversionId) return;
    load();
    historicalConversionService.getSnapshotSourceBinding(conversionId).then(setBinding).catch(() => {});
  }, [conversionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onLineSaved = (line) => {
    if (line) {
      setData((d) => {
        const lines = d.snapshot.site_setup.lines.map((x) => (x.bp_id === line.bp_id ? line : x));
        return { ...d, snapshot: { ...d.snapshot, site_setup: { ...d.snapshot.site_setup, lines, priced: lines.filter((x) => x.price_status === 'priced').length } } };
      });
    }
    load();
  };

  const published = useMemo(() => data?.saved.some((s) => s.status === 'published'), [data]);
  const backUrl = '/stocktaking/import';

  const act = async (action, thenLeave = false) => {
    setBusy(thenLeave ? 'leave' : action);
    try {
      await historicalConversionService.saveClosingSnapshot(conversionId, action, step, ack);
      toast.success(action === 'publish' ? 'Snapshot published' : 'Draft saved');
      if (thenLeave) { navigate(backUrl); return; }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally {
      setBusy(null);
    }
  };

  if (!conversionId) return <div className="p-8" data-testid="snapshot-missing-id">No import selected.</div>;
  if (error?.code === 'source_archive_unavailable') return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 md:p-8" data-testid="legacy-closing-snapshot-page">
      <Link to={backUrl} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="snapshot-back-link"><ArrowLeft className="h-4 w-4" /> Back to imports</Link>
      <SourceUnavailablePanel conversionId={conversionId} detail={error} onRestored={() => { setError(null); load(); }} />
    </div>
  );
  if (error) return <div className="p-8"><Alert variant="destructive" data-testid="snapshot-load-error"><AlertDescription>{typeof error === 'string' ? error : 'Could not load the snapshot'}</AlertDescription></Alert></div>;
  if (!data) return <div className="p-8 flex items-center gap-2 text-slate-500" data-testid="snapshot-loading"><Loader2 className="h-4 w-4 animate-spin" />Loading closing stock…</div>;

  const snap = data.snapshot;
  const ev = snap.evidence;
  const exceptions = snap.exceptions;
  const pendingMatches = data.pending_matches || 0;
  const canWrite = data.writes_allowed && !published && pendingMatches === 0;

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 md:p-8" data-testid="legacy-closing-snapshot-page">
      <Link to={backUrl} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="snapshot-back-link">
        <ArrowLeft className="h-4 w-4" /> Back to imports
      </Link>
      {pendingMatches > 0 && (
        <Alert className="border-amber-300 bg-amber-50" data-testid="snapshot-pending-matches-banner">
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3 text-amber-950">
            <span><strong>{pendingMatches} source products still need matching.</strong> This is a read-only preview: closing stock only includes matched products, and saving, prices and publishing unlock when matching is finished.</span>
            <Link to={matchingPath(data.stage1_run_id, conversionId)}>
              <Button size="sm" data-testid="snapshot-go-to-matching-btn">Continue matching</Button>
            </Link>
          </AlertDescription>
        </Alert>
      )}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-3">
            <CardTitle className="text-2xl">Closing stock snapshot</CardTitle>
            <Badge variant="outline" data-testid="snapshot-status-badge">
              {published ? 'Published' : data.saved.length ? 'Draft' : 'Not saved'}
            </Badge>
            {ev.verified && <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100" data-testid="snapshot-verified-badge"><CheckCircle2 className="mr-1 h-3 w-3" />Confirmed from source</Badge>}
          </div>
          <CardDescription data-testid="snapshot-period">
            Audit {ev.open_date} to {ev.close_date} · closing stock carried into the next new stocktake
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <StatusBanner saved={data.saved} />
          {data.current_rules && !data.current_rules.matches_published && (
            <p className="text-xs text-slate-600" data-testid="snapshot-rules-changed-note">
              Showing the published snapshot exactly as frozen ({snap.site_setup.priced} priced). Current conversion rules
              would price {data.current_rules.priced}; any new prices must be set at site level, not in this snapshot.
            </p>
          )}
          {data.current_rules?.unit_changes?.length > 0 && (() => {
            const ex = data.current_rules.unit_changes.find((c) => parseFloat(c.from) !== 0) || data.current_rules.unit_changes[0];
            return (
            <div className="rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="snapshot-unit-changes-note">
              <strong>Count units read differently under the current rules.</strong> Source quantities and values are identical;
              {' '}{data.current_rules.unit_changes.length} lines are counted in their true container unit (for example
              {' '}{ex.product_name}: {ex.from} → {ex.to}).
              The published snapshot below is shown exactly as frozen.
            </div>
            );
          })()}
          <ProofPanel evidence={ev} />
          <Stepper step={step} setStep={setStep} />
          {step === 1 && <SourceSummary siteName={snap.site_name} s={{
            house: ev.house, name: ev.house_name, open_date: ev.open_date, close_date: ev.close_date, audit_reference: ev.audit_reference,
            structure: ev.structure, excluded_ledgers: ev.structure?.excluded_ledgers,
            locations: (ev.buckets || []).filter((b) => ['location', 'cellar'].includes(b.role)).map((b) => ({ ...b, name: b.name || `Location ${b.code}` })),
            closing_quantity: ev.totals?.closing_qty, closing_value: ev.totals?.closing_value,
            failed_checks: Object.entries(ev.checks || {}).filter(([, ok]) => !ok).map(([k]) => k),
            classification_notes: (ev.buckets || []).filter((b) => b.classification_note).map((b) => `${b.code} ${b.name}: ${b.classification_note}`),
          }} />}
          {step === 1 && <SnapshotSourceStep evidence={ev} buckets={snap.closing.buckets || []} />}
          {step === 1 && binding && <SourceBindingTable binding={binding} />}
          {step === 2 && <SnapshotClosingStep closing={snap.closing} exceptions={exceptions.filter((x) => x.step === 'closing')} />}
          {step === 3 && <SnapshotYtdStep ytd={snap.previous_ytd} previousAudit={ev.previous_audit} status={snap.ytd_status || data.ytd_status} />}
          {step === 4 && <SnapshotSiteStep site={snap.site_setup} assignedNow={data.site_assigned_now} exceptions={exceptions.filter((x) => x.step === 'site')}
            conversionId={conversionId} canEdit={canWrite && !data.frozen} onSaved={onLineSaved} />}
          {step === 4 && <GapReview gaps={snap.gap_summary} acknowledged={ack} onChange={setAck} disabled={!canWrite} />}
          <div className="flex flex-wrap items-center gap-2 border-t pt-4">
            <Button variant="outline" disabled={step === 1} onClick={() => setStep(step - 1)} data-testid="snapshot-prev-btn"><ArrowLeft className="mr-1 h-4 w-4" />Back</Button>
            {step < STEPS.length && <Button variant="outline" onClick={() => setStep(step + 1)} data-testid="snapshot-next-btn">Next<ArrowRight className="ml-1 h-4 w-4" /></Button>}
            <div className="ml-auto flex flex-wrap gap-2">
              <Button variant="ghost" disabled={!!busy} onClick={() => navigate(backUrl)} data-testid="snapshot-cancel-btn">Cancel</Button>
              {data.frozen ? <span className="self-center text-xs text-slate-500" data-testid="snapshot-frozen-note">Published — this snapshot is frozen.</span> : <>
              <Button variant="ghost" disabled={!canWrite || !!busy} onClick={() => act('save_draft', true)} data-testid="snapshot-leave-draft-btn">
                {busy === 'leave' && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Leave as draft
              </Button>
              <Button variant="outline" disabled={!canWrite || !!busy} onClick={() => act('save_draft')} data-testid="snapshot-save-draft-btn">
                {busy === 'save_draft' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}Save draft
              </Button>
              <Button className="bg-slate-900 text-white hover:bg-slate-800" disabled={!canWrite || !ev.verified || step !== STEPS.length || !!busy || (snap.gap_summary?.total > 0 && !ack)} onClick={() => act('publish')} data-testid="snapshot-publish-btn" title={step !== STEPS.length ? 'Review every step first' : (snap.gap_summary?.total > 0 && !ack ? 'Confirm the gap review first' : undefined)}>
                {busy === 'publish' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Lock className="mr-1 h-4 w-4" />}Publish snapshot
              </Button>
              </>}
            </div>
          </div>
          {!data.writes_allowed && <p className="text-xs text-slate-500" data-testid="snapshot-readonly-note">Saving is switched off in this environment.</p>}
        </CardContent>
      </Card>
    </div>
  );
}
